import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/personalize", label: "Personalize" },
  { to: "/vip", label: "VIP / Trade-In" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { cart } = useCart();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(null);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [pathname]);

  // Tiny delay before closing so it doesn't flicker when moving cursor diagonally
  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMenuOpen(true);
  };
  const queueClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setMenuOpen(false);
      setHoveredNav(null);
    }, 120);
  };
  const isShopHover = hoveredNav === "Shop";

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#ffffff]/85 border-b border-[#000000]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 grid grid-cols-3 items-center">
        {/* LEFT — Menu trigger with hover dropdown */}
        <div className="flex items-center gap-2 justify-self-start">
          {/* Desktop trigger */}
          <div
            className="hidden lg:block relative"
            onMouseEnter={openMenu}
            onMouseLeave={queueClose}
          >
            <button
              data-testid="menu-trigger"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              onFocus={openMenu}
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#000000]/90 hover:text-[#c9a84c] transition-colors py-2"
            >
              <span
                aria-hidden="true"
                className="relative flex flex-col gap-[5px] w-[18px]"
              >
                <span className="block h-[1.5px] w-full bg-current" />
                <span className="block h-[1.5px] w-full bg-current" />
              </span>
              Menu
            </button>

            {/* Dropdown — LV-style full-height slide-in mega panel */}
            <div
              data-testid="menu-dropdown"
              className={`fixed top-0 left-0 h-screen flex bg-[#ffffff] shadow-2xl transition-all duration-500 ease-out z-40 ${
                menuOpen
                  ? "opacity-100 translate-x-0 visible"
                  : "opacity-0 -translate-x-6 invisible pointer-events-none"
              }`}
            >
              {/* Column 1 — Close + nav links */}
              <nav className="pt-20 pb-8 min-w-[300px] h-full flex flex-col" role="menu" data-testid="menu-nav">
                <button
                  onClick={() => setMenuOpen(false)}
                  data-testid="menu-close"
                  className="flex items-center gap-3 px-7 pb-8 text-[11px] tracking-[0.28em] uppercase text-black hover:text-[#c9a84c] transition-colors"
                >
                  <span className="text-lg leading-none">✕</span>
                  Close
                </button>
                {navItems.map((n, i) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`dropdown-nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    role="menuitem"
                    onMouseEnter={() => setHoveredNav(n.label)}
                    style={{ transitionDelay: menuOpen ? `${i * 35}ms` : "0ms" }}
                    className={({ isActive }) =>
                      `block px-7 py-3.5 text-[13px] tracking-[0.24em] uppercase transition-all duration-300 ${
                        isActive
                          ? "text-[#c9a84c]"
                          : "text-[#000000]/85 hover:text-[#c9a84c] hover:bg-[#000000]/[0.03]"
                      } ${
                        menuOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
              </nav>

              {/* Columns 2 & 3 — only shown when cursor is on "Shop" */}
              {isShopHover && (
                <>
                  <Link
                    to="/new-arrivals"
                    data-testid="dropdown-nav-new-arrivals"
                    role="menuitem"
                    onMouseEnter={() => setHoveredNav("Shop")}
                    className="group relative w-[320px] h-screen overflow-hidden border-l border-[#000000]/10 block animate-fadeInLeft"
                  >
                    <img
                      src="https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/ucqa1dcp_ChatGPT%20Image%20Aug%2024%2C%202026%2C%2009_38_34%20PM.png"
                      alt="New Arrivals"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <p className="text-[9px] tracking-[0.32em] uppercase text-[#c9a84c]">
                        Just landed
                      </p>
                      <p className="mt-1 font-serif text-white text-xl leading-tight">
                        New Arrivals
                      </p>
                      <p className="mt-1 text-[10px] tracking-[0.24em] uppercase text-white/70">
                        Autumn 2026
                      </p>
                    </div>
                  </Link>

                  <Link
                    to="/shop"
                    data-testid="dropdown-nav-clothing"
                    role="menuitem"
                    onMouseEnter={() => setHoveredNav("Shop")}
                    className="group relative w-[320px] h-screen overflow-hidden border-l border-[#000000]/10 block animate-fadeInLeft"
                    style={{ animationDelay: "80ms" }}
                  >
                    <img
                      src="https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/sdj3ns3l_ChatGPT%20Image%20Aug%2024%2C%202026%2C%2011_09_40%20PM.png"
                      alt="Clothing"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <p className="text-[9px] tracking-[0.32em] uppercase text-[#c9a84c]">
                        Shop the edit
                      </p>
                      <p className="mt-1 font-serif text-white text-xl leading-tight">
                        Clothing
                      </p>
                      <p className="mt-1 text-[10px] tracking-[0.24em] uppercase text-white/70">
                        Jackets · Outerwear
                      </p>
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden text-[#000000] py-2"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>

        {/* CENTER — Logo */}
        <Link
          to="/"
          className="font-serif text-2xl tracking-[0.18em] text-[#000000] text-center justify-self-center"
          data-testid="header-logo"
        >
          BOSCAGE
        </Link>

        {/* RIGHT — Sign in + Cart */}
        <div className="flex items-center gap-6 justify-self-end">
          {user ? (
            <Link
              to="/account"
              data-testid="account-link"
              className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#000000]/80 hover:text-[#c9a84c] transition-colors"
            >
              <User size={16} strokeWidth={1.5} />
              {user.name?.split(" ")[0]}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="login-link"
              className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#000000]/80 hover:text-[#c9a84c] transition-colors"
            >
              <User size={16} strokeWidth={1.5} />
              Sign In
            </Link>
          )}
          <Link
            to="/cart"
            data-testid="cart-link"
            className="relative inline-flex items-center text-[#000000] hover:text-[#c9a84c] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {cart.item_count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-3 bg-[#c9a84c] text-[#000000] text-[10px] font-medium rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center"
              >
                {cart.item_count}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#000000]/10 bg-[#ffffff]"
        >
          <nav className="flex flex-col p-6 gap-5">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                className="text-sm tracking-[0.18em] uppercase text-[#000000]/80"
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to={user ? "/account" : "/login"}
              data-testid="mobile-account-link"
              className="text-sm tracking-[0.18em] uppercase text-[#c9a84c]"
            >
              {user ? `Hello, ${user.name?.split(" ")[0]}` : "Sign In"}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
